import { FC } from 'react'
import type {
  Invoice,
  BusinessInfo,
  ClientInfo,
  PaymentInfo,
} from '@/types/invoice'
import { InvoiceHeader } from './InvoiceHeader'
import { InvoiceLineItems } from './InvoiceLineItems'
import { InvoiceTotals } from './InvoiceTotals'
import { PaymentSection } from './PaymentSection'
import { formatCurrency, formatDate } from '@/utils/format'
import { getPdfUrl } from '@/services/api'

interface InvoiceViewProps {
  invoice: Invoice
  business: BusinessInfo
  client: ClientInfo
  payment: PaymentInfo
  paymentToken: string
  onPaymentSuccess: () => void
}

export const InvoiceView: FC<InvoiceViewProps> = ({
  invoice,
  business,
  client,
  payment,
  paymentToken,
  onPaymentSuccess,
}) => {
  const isPaid = invoice.status === 'paid'
  const isCancelled = invoice.status === 'cancelled'
  const canPay = !isPaid && !isCancelled

  return (
    <div className="min-h-screen py-8 px-4 sm:py-12 sm:px-6">
      <div className="max-w-[650px] mx-auto">
        {/* Invoice Card */}
        <div className="bg-white border border-[#e5e7eb] rounded-lg overflow-hidden">
          {/* Header */}
          <InvoiceHeader
            business={business}
            invoice={invoice}
            client={client}
          />

          {/* Line Items */}
          <div className="px-6 sm:px-8">
            <InvoiceLineItems items={invoice.items} currency={invoice.currency} />
          </div>

          {/* Totals */}
          <div className="px-6 sm:px-8 py-6 border-t border-[#e5e7eb] bg-[#f9fafb]">
          <InvoiceTotals
              subtotal={invoice.subtotal}
              taxRate={invoice.taxRate}
              taxAmount={invoice.taxAmount}
              total={invoice.total}
              currency={invoice.currency}
              totalInUsdc={payment.totalInUsdc}
              paymentToken={payment.paymentToken}
            />
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="px-6 sm:px-8 py-5 border-t border-[#e5e7eb]">
              <p className="text-xs font-medium text-[#6b7280] uppercase tracking-wide mb-2">
                Notes
              </p>
              <p className="text-sm text-[#374151] leading-relaxed">
                {invoice.notes}
              </p>
            </div>
          )}

          {/* Payment Section */}
          {canPay && (
            <div className="px-6 sm:px-8 py-6 border-t border-[#e5e7eb] bg-white">
              <PaymentSection
                paymentToken={paymentToken}
                payment={payment}
                invoiceNumber={invoice.invoiceNumber}
                total={invoice.total}
                currency={invoice.currency}
                onSuccess={onPaymentSuccess}
              />
            </div>
          )}

          {/* Paid Status */}
          {isPaid && (
            <div className="px-6 sm:px-8 py-5 border-t border-[#e5e7eb] bg-[#f0fdf4]">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#16a34a] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#166534]">Paid</span>
                    <span className="text-sm text-[#111827]">{formatCurrency(invoice.total, invoice.currency)}</span>
                    <span className="text-sm text-[#6b7280]">({parseFloat(payment.totalInUsdc).toLocaleString()} {payment.paymentToken})</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {invoice.paidAt && (
                      <span className="text-xs text-[#6b7280]">
                        {formatDate(invoice.paidAt, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                    {invoice.paidSignature && (
                      <>
                        <span className="text-xs text-[#d1d5db]">·</span>
                        <a
                          href={`https://orb.helius.dev/tx/${invoice.paidSignature}?cluster=mainnet-beta`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#16a34a] hover:underline"
                        >
                          View transaction
                        </a>
                      </>
                    )}
                  </div>
                </div>

                <a
                  href={getPdfUrl(paymentToken)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#166534] bg-white border border-[#16a34a]/20 rounded-lg hover:bg-[#f0fdf4] transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Receipt
                </a>
              </div>
            </div>
          )}

          {/* Cancelled Status */}
          {isCancelled && (
            <div className="px-6 sm:px-8 py-5 border-t border-[#e5e7eb] bg-[#f3f4f6]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#6b7280] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <span className="text-sm font-semibold text-[#374151]">Cancelled</span>
                  <p className="text-xs text-[#6b7280]">This invoice has been cancelled</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-[#9ca3af]">
            Powered by{' '}
            <a
              href="https://soljar.xyz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#6b7280] hover:text-[#111827]"
            >
              soljar
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
